/**
 * EntityFactory - Entity 생성을 위한 Factory 클래스
 * Builder 패턴을 통해 각 Entity 타입을 생성합니다.
 */
import { SHAPES } from '../../const'
import type { ICanvasEngineEntityController } from '../../types/interface'
import type { CanvasEngineExportEntityType } from '../../types'
import type { ILine } from '../../backend/IRenderer'

import { PointBuilder } from '../types/point'
import { DirectionBuilder } from '../types/direction'
import { RectBuilder } from '../types/rect'
import { TripodBuilder } from '../types/tripod'
import { BrushBuilder } from '../types/brush'
import { CanvasEngineEntity } from '../index'

/**
 * Entity Builder 타입
 */
export type EntityBuilder =
  | PointBuilder
  | DirectionBuilder
  | RectBuilder
  | TripodBuilder
  | BrushBuilder

/**
 * EntityFactory 클래스
 */
export class EntityFactory {
  private canvasEngine: ICanvasEngineEntityController

  /**
   * EntityFactory 생성자
   * @param canvasEngine - CanvasEngine 컨트롤러
   */
  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
  }

  /**
   * Entity 타입에 맞는 Builder 반환
   * @param type - Entity 타입
   * @returns 해당 타입의 Builder
   */
  getBuilder(type: SHAPES): EntityBuilder {
    switch (type) {
      case SHAPES.POINT:
        return new PointBuilder(this.canvasEngine)
      case SHAPES.DIRECTION:
        return new DirectionBuilder(this.canvasEngine)
      case SHAPES.RECT:
        return new RectBuilder(this.canvasEngine)
      case SHAPES.TRIPOD:
        return new TripodBuilder(this.canvasEngine)
      case SHAPES.BRUSH:
        return new BrushBuilder(this.canvasEngine)
      default:
        throw new Error(`Unsupported entity type: ${type}`)
    }
  }

  /**
   * Export 데이터에서 Builder 생성
   * @param data - Export 데이터
   * @returns 해당 타입의 Builder (데이터가 로드된 상태)
   */
  getBuilderFromExportData(data: CanvasEngineExportEntityType): EntityBuilder {
    switch (data.type) {
      case SHAPES.POINT:
        return new PointBuilder(this.canvasEngine).fromExportData(data)
      case SHAPES.DIRECTION:
        return new DirectionBuilder(this.canvasEngine).fromExportData(data)
      case SHAPES.RECT:
        return new RectBuilder(this.canvasEngine).fromExportData(data)
      case SHAPES.TRIPOD:
        return new TripodBuilder(this.canvasEngine).fromExportData(data)
      case SHAPES.BRUSH:
        return new BrushBuilder(this.canvasEngine).fromExportData(data)
      default:
        throw new Error(`Unsupported entity type: ${data.type}`)
    }
  }

  /**
   * Export 데이터에서 직접 Entity 생성
   * @param data - Export 데이터
   * @param interactionMode - Entity 상호작용 모드
   * @returns 생성된 CanvasEngineEntity
   */
  createEntityFromExportData(
    data: CanvasEngineExportEntityType,
    interactionMode: boolean = true,
  ): CanvasEngineEntity {
    // Builder를 사용하여 Entity 생성
    const builder = this.getBuilderFromExportData(data)

    // interactionMode 설정
    builder.setInteractionMode(interactionMode)

    const initConfig = builder.build()

    // Builder의 InitConfig를 CanvasEngineEntity 생성자 형식으로 변환
    // InitConfig는 'config' 속성을 사용하지만, Entity 생성자는 'createConfig'를 기대함
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entityConfig: any = {
      ...initConfig,
      type: data.type,
      createConfig: initConfig.config, // config -> createConfig로 변환
      balloonTag: data.balloonTag,
    }

    // config 속성은 제거 (중복 방지)
    delete entityConfig.config

    // CanvasEngineEntity 생성
    const entity = new CanvasEngineEntity(this.canvasEngine, entityConfig)

    // 타입별 추가 로직 실행
    switch (data.type) {
      case SHAPES.POINT:
      case SHAPES.RECT:
      case SHAPES.TRIPOD:
        // Balloon 위치 설정
        if (data.balloonPoint) {
          entity.moveBallon(data.balloonPoint)
        }
        break

      case SHAPES.DIRECTION:
        // Balloon 위치 설정
        if (data.balloonPoint) {
          entity.moveBallon(data.balloonPoint)
        }
        break

      case SHAPES.BRUSH:
        // Brush hitStrokeWidth 설정
        if (entity.getBrush()) {
          const lines: ILine[] = entity.getBrush()!.find('Line')
          const visibleLines = lines.filter(
            (line: ILine) => line.opacity() !== 0 && line.stroke() !== 'transparent',
          )
          if (visibleLines.length > 0) {
            const firstLine = visibleLines[0]
            const hitStrokeWidth = (firstLine.strokeWidth() || 0) + 50
            visibleLines.forEach((line: ILine) => {
              try {
                // hit 굵기만 지정(생성 시 strokeScale 히트 경로는 유지)
                line.hitArea({ kind: 'strokeWidth', px: hitStrokeWidth })
              } catch (error) {
                console.warn('hitStrokeWidth not supported for this line:', error)
              }
            })
          }
        }
        break
    }

    // TRIPOD 전용: tailStyle 설정
    if (data.type === SHAPES.TRIPOD && data.tripodShapePoints?.tailStyle) {
      entity.setIndividualTailStyle(data.tripodShapePoints.tailStyle)
    }

    return entity
  }

  /**
   * Point Builder 반환
   * @returns PointBuilder
   */
  createPointBuilder(): PointBuilder {
    return new PointBuilder(this.canvasEngine)
  }

  /**
   * Direction Builder 반환
   * @returns DirectionBuilder
   */
  createDirectionBuilder(): DirectionBuilder {
    return new DirectionBuilder(this.canvasEngine)
  }

  /**
   * Rect Builder 반환
   * @returns RectBuilder
   */
  createRectBuilder(): RectBuilder {
    return new RectBuilder(this.canvasEngine)
  }

  /**
   * Tripod Builder 반환
   * @returns TripodBuilder
   */
  createTripodBuilder(): TripodBuilder {
    return new TripodBuilder(this.canvasEngine)
  }

  /**
   * Brush Builder 반환
   * @returns BrushBuilder
   */
  createBrushBuilder(): BrushBuilder {
    return new BrushBuilder(this.canvasEngine)
  }

  /**
   * 간편 생성 메서드: ID와 타입으로 Entity 생성
   * @param type - Entity 타입
   * @param config - Entity 생성 설정
   * @returns 생성된 CanvasEngineEntity
   */
  createEntity(
    type: SHAPES,
    config: {
      id: string
      createPoint: { x: number; y: number }
      createConfig?: Record<string, unknown>
      layerId?: string
      balloonFontColor?: string
      balloonFillColor?: string
      balloonScale?: number
      connectedLineThicknessScale?: number
      balloonFontScale?: number
      pointShapeScale?: number
      areaBorderThicknessScale?: number
      points?: number[]
      brushLines?: Array<{ points: number[]; strokeWidth: number; stroke: string }>
    },
  ): CanvasEngineEntity {
    // CanvasEngineEntity 생성자에 전달할 initConfig 구성
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entityConfig: any = {
      id: config.id,
      type,
      createPoint: config.createPoint,
    }

    // 선택적 속성 추가
    if (config.createConfig) entityConfig.createConfig = config.createConfig
    if (config.layerId) entityConfig.layerId = config.layerId
    if (config.balloonFontColor) entityConfig.balloonFontColor = config.balloonFontColor
    if (config.balloonFillColor) entityConfig.balloonFillColor = config.balloonFillColor
    if (config.balloonScale !== undefined) entityConfig.balloonScale = config.balloonScale
    if (config.connectedLineThicknessScale !== undefined)
      entityConfig.connectedLineThicknessScale = config.connectedLineThicknessScale
    if (config.balloonFontScale !== undefined)
      entityConfig.balloonFontScale = config.balloonFontScale
    if (config.pointShapeScale !== undefined) entityConfig.pointShapeScale = config.pointShapeScale
    if (config.areaBorderThicknessScale !== undefined)
      entityConfig.areaBorderThicknessScale = config.areaBorderThicknessScale
    if (config.points) entityConfig.points = config.points
    if (config.brushLines) entityConfig.brushLines = config.brushLines

    // CanvasEngineEntity 직접 생성
    return new CanvasEngineEntity(this.canvasEngine, entityConfig)
  }
}
